/**
 * Simulated GET/POST /api/coaching/operations — Operations Coaching on
 * Company → Operations Summary.
 *
 * Shape is `CoachingResponse` from src/lib/api/coaching.ts.
 *
 * NO AI CALL IS MADE. The production endpoint asks Claude to grade each
 * supervisor; the demo has no backend and must not pretend to have one, so the
 * grade is a deterministic score and the justification is assembled from real
 * numbers about that supervisor's own jobs.
 *
 * Derived from the snapshot
 *   • Who gets graded: supervisors that both appear as `v_jobs_full`
 *     .supervisor_name and have a `profiles` row (or carry the `supervisor`
 *     staff role), ranked by how many open jobs they carry.
 *   • Every figure quoted in a justification — open jobs, average age in days,
 *     outstanding balance — is computed from that supervisor's real jobs.
 *
 * Generated: the score itself (seeded per supervisor per period, so the trend
 * line is stable) and the sentence templates.
 */
import { loadTable } from '~/demo/data';
import { seededFloat, seededInt } from './prng';
import { calendarDaysSince, num, round2, str } from './finance';

export type CoachingWindow = 'daily' | 'weekly' | 'monthly';

/**
 * Short forms the Operations Summary grid keys its fixed cards on
 * (`COACHING_SUBJECTS` in OperationsSummary.tsx). A supervisor is only renamed
 * when the short form is genuinely a prefix of their real first name, so no
 * name is invented — "Christopher" → "Chris", "Leonardo" → "Leo".
 */
const SHORT_FORMS = ['Chris', 'David', 'Leo', 'Jade', 'Sam'];

interface Supervisor {
  scope: string;
  name: string;
  openJobs: number;
  avgAgeDays: number;
  balanceDue: number;
  invoicedJobs: number;
}

function shortName(full: string): string {
  const first = full.trim().split(/\s+/)[0] ?? full;
  const hit = SHORT_FORMS.find((s) => first.toLowerCase().startsWith(s.toLowerCase()));
  return hit ?? first;
}

function gradeFor(score: number): string {
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  return 'D';
}

/** Local YYYY-MM-DD — the Dates below are built locally, so format them locally. */
const isoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Monday of the week containing `d`, as YYYY-MM-DD. */
function mondayOf(d: Date): string {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = out.getDay();
  out.setDate(out.getDate() + (dow === 0 ? -6 : 1 - dow));
  return isoDay(out);
}

function periodsFor(win: CoachingWindow, count: number): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    if (win === 'daily') {
      const d = new Date(now); d.setDate(d.getDate() - i);
      if (d.getDay() === 0 || d.getDay() === 6) { count += 1; continue; }
      out.push(isoDay(d));
    } else if (win === 'monthly') {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push(isoDay(d));
    } else {
      const d = new Date(now); d.setDate(d.getDate() - i * 7);
      out.push(mondayOf(d));
    }
  }
  return out;
}

async function supervisors(): Promise<Supervisor[]> {
  const [jobs, profiles] = await Promise.all([loadTable('v_jobs_full'), loadTable('profiles')]);
  const known = new Set(profiles.map((p) => str(p.display_name)).filter(Boolean));
  for (const p of profiles) {
    const roles = Array.isArray(p.staff_role_keys) ? (p.staff_role_keys as unknown[]).map(String) : [];
    if (roles.includes('supervisor')) known.add(str(p.display_name));
  }

  const agg = new Map<string, { open: number; ageDays: number; balance: number; invoiced: number }>();
  for (const j of jobs) {
    const sup = str(j.supervisor_name);
    if (!sup || !known.has(sup)) continue;
    if (j.status_is_terminal === true) continue;
    const cur = agg.get(sup) ?? { open: 0, ageDays: 0, balance: 0, invoiced: 0 };
    cur.open += 1;
    cur.ageDays += calendarDaysSince(str(j.received_at) || str(j.created_at));
    cur.balance += num(j.balance_due);
    if (num(j.total_invoiced) > 0) cur.invoiced += 1;
    agg.set(sup, cur);
  }

  return [...agg.entries()]
    .sort((a, b) => b[1].open - a[1].open)
    .slice(0, 5)
    .map(([scope, v]) => ({
      scope,
      name: shortName(scope),
      openJobs: v.open,
      avgAgeDays: v.open ? Math.round(v.ageDays / v.open) : 0,
      balanceDue: round2(v.balance),
      invoicedJobs: v.invoiced,
    }));
}

const ACTIONS = [
  'Close out the two oldest jobs before taking new work — age is the whole gap here.',
  'Get invoices out the day work completes; the backlog is sitting on billing, not production.',
  'Chase the outstanding balances over 60 days with the collector before Friday.',
  'Walk the oldest open file with the customer and set a completion date in writing.',
  'Tighten daily notes — half these files have no update in the last week.',
];

function reportFor(sup: Supervisor, period: string, win: CoachingWindow) {
  const score = seededInt(`${sup.scope}|${period}|${win}`, 62, 97);
  const grade = gradeFor(score);
  const action = ACTIONS[seededInt(`${sup.scope}|${period}|action`, 0, ACTIONS.length - 1)];
  const justification =
    `${sup.openJobs} open job${sup.openJobs === 1 ? '' : 's'}, averaging ${sup.avgAgeDays} days old, `
    + `with ${sup.balanceDue > 0 ? `$${Math.round(sup.balanceDue).toLocaleString('en-US')} still outstanding` : 'nothing outstanding'} `
    + `across ${sup.invoicedJobs} invoiced file${sup.invoicedJobs === 1 ? '' : 's'}.`;
  return {
    scope: sup.scope,
    name: sup.name,
    grade,
    score,
    justification,
    topAction: action,
    jobCount: sup.openJobs,
    body:
      `${sup.scope} — ${grade} (${score}/100) for the ${win === 'daily' ? 'day' : win === 'monthly' ? 'month' : 'week'} of ${period}.\n\n`
      + `${justification}\n\nTop action: ${action}\n\n`
      + 'Generated for the demo from the job snapshot — no AI grader ran.',
    generatedAt: `${period}T07:00:00Z`,
    trend: (['up', 'down', 'steady'] as const)[seededInt(`${sup.scope}|${period}|trend`, 0, 2)],
  };
}

function overallFor(sups: Supervisor[], period: string, win: CoachingWindow) {
  const openJobs = sups.reduce((s, x) => s + x.openJobs, 0);
  const balance = sups.reduce((s, x) => s + x.balanceDue, 0);
  const avgAge = sups.length ? Math.round(sups.reduce((s, x) => s + x.avgAgeDays, 0) / sups.length) : 0;
  const score = sups.length
    ? Math.round(sups.reduce((s, x) => s + seededInt(`${x.scope}|${period}|${win}`, 62, 97), 0) / sups.length)
    : seededInt(`overall|${period}`, 70, 92);
  return {
    scope: 'overall',
    name: 'Overall',
    grade: gradeFor(score),
    score,
    justification:
      `${openJobs} open jobs across ${sups.length} supervisors, average age ${avgAge} days, `
      + `$${Math.round(balance).toLocaleString('en-US')} outstanding.`,
    topAction: 'Work the oldest decile of the board first — the age curve, not the intake, is what moves the grade.',
    jobCount: openJobs,
    body: 'Roll-up of the per-supervisor grades below. Generated for the demo from the job snapshot — no AI grader ran.',
    generatedAt: `${period}T07:00:00Z`,
    trend: 'steady' as const,
  };
}

export async function simulatedCoaching(win: CoachingWindow = 'weekly') {
  const sups = await supervisors();
  const periods = periodsFor(win, 8);
  const current = periods[0];

  const history = periods.map((period) => ({
    week: period,
    reports: [overallFor(sups, period, win), ...sups.map((s) => reportFor(s, period, win))],
    summary: {
      body: summaryBody(sups, period, win),
      generatedAt: `${period}T07:05:00Z`,
    },
  }));

  const weeks = [...periods].reverse();
  const trend = {
    weeks,
    series: [{ scope: 'overall', name: 'Overall', scores: weeks.map((p) => overallFor(sups, p, win).score) },
      ...sups.map((s) => ({
        scope: s.scope,
        name: s.name,
        scores: weeks.map((p) => reportFor(s, p, win).score as number | null),
      }))],
  };

  return {
    ok: true,
    simulated: true,
    demo_note:
      'Grades A–D are deterministic scores per supervisor per period; the supervisors and every number quoted in a '
      + 'justification (open jobs, average age, outstanding balance) come from v_jobs_full and profiles. No AI call is made.',
    period: current,
    window: win,
    grades: history[0]?.reports ?? [],
    summary: history[0]?.summary ?? null,
    trend,
    history,
  };
}

function summaryBody(sups: Supervisor[], period: string, win: CoachingWindow): string {
  const openJobs = sups.reduce((s, x) => s + x.openJobs, 0);
  const balance = Math.round(sups.reduce((s, x) => s + x.balanceDue, 0));
  const worst = [...sups].sort((a, b) => b.avgAgeDays - a.avgAgeDays)[0];
  const best = [...sups].sort((a, b) => a.avgAgeDays - b.avgAgeDays)[0];
  const label = win === 'daily' ? 'day' : win === 'monthly' ? 'month' : 'week';
  const pace = seededFloat(`${period}|pace`, 0.9, 1.15);
  return (
    `Operations roll-up for the ${label} of ${period}. ${openJobs} open jobs across ${sups.length} supervisors, `
    + `$${balance.toLocaleString('en-US')} outstanding. `
    + (worst ? `${worst.name} carries the oldest board at ${worst.avgAgeDays} days average age; ` : '')
    + (best ? `${best.name} is cleanest at ${best.avgAgeDays} days. ` : '')
    + `Throughput is running ${pace >= 1 ? 'ahead of' : 'behind'} the trailing average. `
    + 'SIMULATED: assembled from the job snapshot for the demo — no AI summary was generated.'
  );
}

/** POST /api/coaching/operations — "Generate grades" / "Operations Summary". */
export async function simulatedCoachingGenerate(mode: 'grades' | 'summary', win: CoachingWindow = 'weekly') {
  const data = await simulatedCoaching(win);
  if (mode === 'summary') {
    return { ok: true, simulated: true, demo_note: data.demo_note, summary: data.summary };
  }
  return { ok: true, simulated: true, demo_note: data.demo_note, graded: data.grades.length };
}
